import { useEffect, useState } from 'react';
import styles from './UpdateToast.module.css';
import Icon from './Icon';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Long-lived tabs never re-request index.html, so a deploy would go unnoticed
// until the next hard navigation. Polls dist/version.json (emitted per build,
// fetched with cache: no-store so no cache layer can serve a stale copy) and
// offers a refresh when the deployed build differs from the running one.
// Deliberately a button, not an auto-reload — a reload mid-upload or mid-edit
// would lose work.
const UpdateToast = () => {
  // Dismissal is per-BUILD, not a boolean: "Later" on build B must not
  // silence the toast for build C — the whole audience of this feature is
  // tabs that stay open across many deploys.
  const [latestBuild, setLatestBuild] = useState<string | null>(null);
  const [dismissedBuild, setDismissedBuild] = useState<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const v = (await res.json()) as { build?: string };
        if (!stopped && v.build && v.build !== __APP_BUILD__) setLatestBuild(v.build);
      } catch {
        // Offline / transient — try again on the next tick.
      }
    };
    const id = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    check();
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!latestBuild || latestBuild === dismissedBuild) return null;

  return (
    <div className={styles.toast}>
      <Icon name="rocket_launch" style={{ fontSize: 18 }} />
      <span>A new version is available</span>
      <button className={styles.refreshBtn} onClick={() => window.location.reload()}>
        Refresh
      </button>
      <button className={styles.dismissBtn} onClick={() => setDismissedBuild(latestBuild)} title="Later">
        <Icon name="close" style={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

export default UpdateToast;
